import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import {
    ButtonComponent,
    DialogComponent,
    DialogDescriptionComponent,
} from '@interacta-shared/ui';
import { filterMap } from '@interacta-shared/util';
import { CommunityType } from '@interacta-shared/util-caronte';
import { BasePostLite } from '@interacta-shared/util-post-lite';
import { ICustomFieldDefinition } from '@interacta/util-community';
import { CustomOrActivityPost } from '@interacta/util-post';
import { RelatedLinkedBy } from '@modules/communities/models/related-item/related-item.model';
import { AppState } from '@modules/core/store';
import { PostFiltersModule } from '@modules/post-filters/post-filters.module';
import { RelationsEnabledPost } from '@modules/post-relations/models/post-relations.model';
import { IPostFilters } from '@modules/post/models/filter-post/filter-post.model';
import { PostModule } from '@modules/post/post.module';
import { RelationsDialogPage } from '@modules/relations-dialog/model/relations-dialog.model';
import {
    activityAddon,
    RelationDialogConfig,
} from '@modules/relations-dialog/relation-dialog.config';
import { computeRelationDialogSelectablePostsFilter } from '@modules/relations-dialog/relations-dialog.utils';
import { RelationsDialogActions } from '@modules/relations-dialog/store';
import * as RelationsDialogSelectors from '@modules/relations-dialog/store/relations-dialog.selectors';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { RelationsDialogCommunityComponent } from '../relations-dialog-community/relations-dialog-community.component';
import { RelationsDialogFieldComponent } from '../relations-dialog-field/relations-dialog-field.component';
import { RelationsDialogLinkResultsComponent } from '../relations-dialog-link-results/relations-dialog-link-results.component';

@Component({
    selector: 'interacta-relations-dialog',
    templateUrl: './relations-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslatePipe,
        ButtonComponent,
        DialogComponent,
        DialogDescriptionComponent,
        ButtonComponent,
        RelationsDialogCommunityComponent,
        RelationsDialogLinkResultsComponent,
        RelationsDialogFieldComponent,
        PostModule, // per interacta-post-fast-create
        PostFiltersModule, // per interacta-post-picker-advanced
    ],
})
export class RelationsDialogComponent {
    private readonly store = inject(Store<AppState>);
    private readonly gaService = inject(GoogleAnalyticsService);

    posts = this.store.selectSignal(RelationsDialogSelectors.posts);
    page = toSignal(
        this.store
            .select(RelationsDialogSelectors.page)
            .pipe(filterMap((page) => page ?? undefined)),
        { initialValue: 'create' },
    );

    readonly linkedCommunityType = computed(
        () => this.item()?.linkedBy.community.type ?? CommunityType.CUSTOM,
    );

    readonly activityAddonLabel = computed(() =>
        activityAddon(this.linkedCommunityType()),
    );

    readonly RelationDialogConfig = computed(() =>
        RelationDialogConfig(this.linkedCommunityType()),
    );

    readonly isOpen = this.store.selectSignal(RelationsDialogSelectors.isOpen);
    readonly item = this.store.selectSignal(RelationsDialogSelectors.item);

    readonly linkResults = this.store.selectSignal(
        RelationsDialogSelectors.linkResults,
    );
    readonly sourcePost = this.store.selectSignal(
        RelationsDialogSelectors.sourcePost,
    );
    readonly inverseLinks = this.store.selectSignal(
        RelationsDialogSelectors.inverseLinks,
    );
    readonly isLinkingPost = this.store.selectSignal(
        RelationsDialogSelectors.isLinkingPost,
    );

    readonly pageToOpenAfterFieldChoice = this.store.selectSignal(
        RelationsDialogSelectors.nextPage,
    );

    selectLink(item: RelatedLinkedBy): void {
        const nextPage = this.pageToOpenAfterFieldChoice() ?? 'create';

        let page = nextPage;
        if (
            item.linkedBy.postLinkFieldDefinitions &&
            item.linkedBy.postLinkFieldDefinitions.length > 1
        ) {
            page = 'field';
        } else {
            this.store.dispatch(RelationsDialogActions.resetNextPage());
        }

        this.store.dispatch(
            RelationsDialogActions.changePage({
                page,
                item,
            }),
        );
    }

    back(): void {
        this.store.dispatch(RelationsDialogActions.back());
    }

    close(): void {
        this.store.dispatch(RelationsDialogActions.close());
    }

    linkToPosts(
        sourcePost: RelationsEnabledPost,
        posts: CustomOrActivityPost[],
        field: ICustomFieldDefinition,
    ): void {
        this.store.dispatch(
            RelationsDialogActions.linkToPosts({
                sourcePost,
                posts,
                field,
            }),
        );
    }

    changePage(page: RelationsDialogPage): void {
        if (page === 'create')
            this.gaService.sendCustomEvent(
                'isp_web_relations_dialog_create_post_clicked',
            );
        if (page === 'link')
            this.gaService.sendCustomEvent(
                'isp_web_relations_dialog_link_post_clicked',
            );

        this.store.dispatch(RelationsDialogActions.changePage({ page }));
    }

    loadInversePosts(
        item: RelatedLinkedBy,
        filters: IPostFilters,
        sourcePost: BasePostLite,
    ): void {
        const apiFilters = computeRelationDialogSelectablePostsFilter(
            item,
            sourcePost,
        );
        if (apiFilters) {
            this.store.dispatch(
                RelationsDialogActions.fetchInverseLinks({
                    item,
                    filters,
                    apiFilters,
                }),
            );
        }
    }
}
